// CaseStudies.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CASES } from "../pages/caseData";
import "../styles/CaseStudies.css";

/** sanitize filename for download */
function safeFilename(name = "case-study") {
  return name
    .toString()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-_.]/g, "") // removed unnecessary escape
    .replace(/-+/g, "-")
    .toLowerCase();
}

/**
 * downloadFileSafe(url, suggestedName)
 * - fetches the resource and logs status & content-type
 * - checks the first bytes for "%PDF" and content-type for "pdf"
 * - if valid, triggers a download via blob URL
 * - otherwise opens the URL in a new tab and logs the response text to console
 */
async function downloadFileSafe(url, suggestedName = "case-study.pdf") {
  try {
    console.log("[download] fetching:", url);
    const resp = await fetch(url, { cache: "no-store" });
    console.log("[download] status:", resp.status);
    const ctype = (resp.headers.get("content-type") || "").toLowerCase();
    console.log("[download] content-type:", ctype);

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      console.error("[download] non-OK response body (first 2000 chars):", text.slice(0, 2000));
      alert(`Failed to fetch file (status ${resp.status}). Opening in new tab as fallback. See console for details.`);
      window.open(url, "_blank", "noopener");
      return;
    }

    const arrBuf = await resp.arrayBuffer();
    const firstBytes = new Uint8Array(arrBuf).slice(0, 8);
    const ascii = Array.from(firstBytes)
      .map((b) => (b >= 32 && b <= 126 ? String.fromCharCode(b) : "."))
      .join("");
    console.log("[download] first bytes ascii-ish:", ascii);

    // If content-type or first bytes don't indicate PDF, show debug info and fallback
    if (!ascii.includes("%PDF") && !ctype.includes("pdf")) {
      const text = await new Response(arrBuf).text().catch(() => "");
      console.error("[download] response is NOT a PDF. First 2000 chars:", text.slice(0, 2000));
      alert("Server returned non-PDF content (check console). Opening in new tab as fallback.");
      window.open(url, "_blank", "noopener");
      return;
    }

    // Looks like a PDF — trigger download
    const blob = new Blob([arrBuf], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    console.log("[download] download started:", suggestedName);
  } catch (err) {
    console.error("[download] error:", err);
    // fallback: open in new tab so user can still access it
    try {
      window.open(url, "_blank", "noopener");
    } catch (e) {
      console.error("[download] fallback open failed:", e);
    }
    alert("Unable to download file directly — opened in a new tab instead (check console for details).");
  }
}

export default function CaseStudies({ heroImage = "" }) {
  const navigate = useNavigate();

const handleKnowMore = (c) => {
  if (c.pdfUrl) {
    window.open(c.pdfUrl, "_blank", "noopener,noreferrer");
  }
};



  return (
    <div className="cs-page">
      <header
        className="cs-hero"
        style={{ backgroundImage: `url(${heroImage || "https://user-gen-media-assets.s3.amazonaws.com/gemini_images/98293c3d-230c-45a6-94fd-3cce7752eacf.png"})` }}
        role="img"
        aria-label="Case studies hero"
      >
        <div className="cs-hero-overlay" />
        <div className="cs-hero-inner">
        </div>

        <svg className="cs-hero-wave" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden>
          <path d="M0,40 C360,120 1080,-40 1440,40 L1440,80 L0,80 Z" fill="#ffffff" opacity="0.98" />
        </svg>
      </header>

      <main className="cs-container">
        <p className="cs-lead">
          Real projects, measurable outcomes. Browse success stories where Sprout’s programs moved the needle
          — improved engagement, productivity and learning outcomes.
        </p>

        <section className="cs-grid" aria-label="Case studies list">
          {CASES.map((c) => (
            <article className="cs-card" key={c.id}>
              <div className="cs-card-body">
                <h3 className="cs-card-title">{c.title}</h3>
                <p className="cs-card-excerpt">{c.excerpt}</p>
                <div className="cs-card-actions">
                  <button
                    className="cs-btn cs-link"
                    onClick={() => handleKnowMore(c)}
                    aria-label={c.pdfUrl ? "Download case study PDF" : `Open ${c.title}`}
                    type="button"
                  >
                    Know more
                  </button>
                </div>
              </div>
            </article>
          ))}
        </section>
      </main>
    </div>
  );
}